import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

type IconMap = Record<string, string>

let iconsPromise: Promise<IconMap> | null = null
let iconsCache: IconMap | null = null
const listeners = new Set<() => void>()

export async function loadLucideIcons(): Promise<IconMap> {
  if (iconsCache) return iconsCache
  if (!iconsPromise) {
    iconsPromise = import('./lucideIcons.json').then((m) => {
      iconsCache = m.default as IconMap
      listeners.forEach((cb) => cb())
      return iconsCache
    })
  }
  return iconsPromise
}

function useLucideIcons(): IconMap | null {
  const [icons, setIcons] = useState<IconMap | null>(iconsCache)

  useEffect(() => {
    if (iconsCache) {
      setIcons(iconsCache)
      return
    }
    const cb = () => setIcons(iconsCache)
    listeners.add(cb)
    loadLucideIcons()
    return () => {
      listeners.delete(cb)
    }
  }, [])

  return icons
}

interface LucideIconProps {
  name: string
  className?: string
}

export function LucideIcon({ name, className }: LucideIconProps) {
  const icons = useLucideIcons()
  const inner = icons?.[name]

  if (!inner) {
    return <span className={cn('inline-block', className)} aria-hidden="true" />
  }

  return (
    <span
      className={cn('inline-flex items-center justify-center', className)}
      aria-hidden="true"
      dangerouslySetInnerHTML={{
        __html: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`,
      }}
    />
  )
}
